import React, { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { Link } from 'react-router-dom';
import { MapPin, Coffee, Users, Award, CheckCircle, ArrowRight } from 'lucide-react';
import { coffeeCivilization } from '../data/tours';

export const CoffeeCivilizationPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country: '',
    travelPeriod: '',
    travelers: '',
    interests: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Handle form submission - integrate with existing inquiry system
    console.log('Coffee Civilization Inquiry:', formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div>
      <Navigation />
      <WhatsAppButton variant="floating" />

      {/* STORY: Hero Section */}
      <section style={{
        position: 'relative',
        minHeight: '80vh',
        background: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${coffeeCivilization.image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        color: 'white'
      }}>
        <div className="container" style={{ maxWidth: '900px', padding: '0 24px' }}>
          <div style={{
            fontSize: '14px',
            letterSpacing: '2px',
            color: '#FBBF24',
            marginBottom: '16px',
            textTransform: 'uppercase'
          }}>
            Indotravelista Presents
          </div>
          <h1 style={{
            fontSize: 'clamp(32px, 6vw, 56px)',
            fontWeight: '400',
            marginBottom: '24px',
            lineHeight: '1.2'
          }}>
            {coffeeCivilization.title}
          </h1>
          <p style={{
            fontSize: 'clamp(16px, 2vw, 20px)',
            color: 'rgba(255,255,255,0.9)',
            marginBottom: '32px',
            lineHeight: '1.6'
          }}>
            {coffeeCivilization.subtitle}
          </p>
          <p style={{
            fontSize: '16px',
            color: 'rgba(255,255,255,0.85)',
            maxWidth: '700px',
            margin: '0 auto 40px',
            lineHeight: '1.8'
          }}>
            Journey through Indonesia's volcanic terroirs from Sumatra to Papua, tracing the origins, heritage, and contemporary craft of one of the world's most celebrated coffee-producing nations. This is not a coffee tour—it is an immersion into Indonesia's living coffee civilization.
          </p>
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <a 
              href="#inquiry"
              style={{
                padding: '16px 32px',
                background: '#FBBF24',
                color: 'var(--bg-primary)',
                textDecoration: 'none',
                fontWeight: '600',
                borderRadius: '4px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Plan Your Coffee Journey
            </a>
          </div>
        </div>
      </section>

      {/* STORY: The Narrative */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '24px' }}>
            Indonesia's Coffee Story
          </h2>
          <div style={{ color: 'var(--text-secondary)', fontSize: '16px', lineHeight: '1.8', marginBottom: '32px' }}>
            <p style={{ marginBottom: '20px' }}>
              Indonesia's relationship with coffee spans centuries—from Dutch colonial plantations to indigenous cultivation, from traditional processing methods passed through generations to the contemporary specialty coffee movement transforming the global industry.
            </p>
            <p style={{ marginBottom: '20px' }}>
              Across volcanic slopes, highland valleys, and remote island estates, Indonesia produces some of the world's most distinctive single-origin coffees. Each region—from Sumatra's earthy Mandheling to Java's heritage arabica, from Flores' volcanic sweetness to Papua's wild complexity—tells a story of terroir, culture, and craft.
            </p>
            <p style={{ marginBottom: '20px' }}>
              Coffee Civilization is your gateway into this world. More than plantation visits or cupping sessions, this journey connects heritage and innovation, origin and transformation, people and place. It is an exploration for those who seek to understand coffee not as a commodity, but as a civilization.
            </p>
          </div>
        </div>
      </section>

      {/* EXPERIENCES */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '48px' }}>
            Coffee Civilization Experiences
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                icon: <Coffee size={32} />,
                title: 'Origin Immersion',
                description: 'Visit working coffee plantations across volcanic terroirs. Witness harvesting, processing, and the journey from cherry to green bean.'
              },
              {
                icon: <Users size={32} />,
                title: 'Producer Relationships',
                description: 'Meet smallholder farmers, estate managers, and specialty roasters. Understand the economics, challenges, and passion behind Indonesian coffee.'
              },
              {
                icon: <MapPin size={32} />,
                title: 'Regional Diversity',
                description: 'Experience the distinct character of Sumatran, Javanese, Flores, and other regional coffees through comparative tastings and origin visits.'
              },
              {
                icon: <Award size={32} />,
                title: 'Professional Cupping',
                description: 'Learn sensory evaluation with expert Q-graders. Develop your palate and understanding of quality assessment, flavor profiling, and processing influence.'
              }
            ].map((exp, index) => (
              <div key={index} style={{
                padding: '32px',
                background: 'var(--bg-primary)',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                borderRadius: '4px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#FBBF24';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
                <div style={{ color: '#FBBF24', marginBottom: '16px' }}>
                  {exp.icon}
                </div>
                <h3 style={{ fontSize: '20px', marginBottom: '12px', color: 'var(--text-primary)' }}>
                  {exp.title}
                </h3>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  {exp.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ITINERARY */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '16px' }}>
            Sample Journey: {coffeeCivilization.duration}
          </h2>
          <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '48px', maxWidth: '700px', margin: '0 auto 48px' }}>
            A curated expedition across {coffeeCivilization.destination}, exploring volcanic terroirs, heritage plantations, and contemporary specialty coffee culture.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {coffeeCivilization.itinerary.map((day, index) => (
              <div key={index} style={{
                display: 'flex',
                gap: '24px',
                padding: '24px',
                background: 'var(--bg-secondary)',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                borderRadius: '4px'
              }}>
                <div style={{
                  minWidth: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: '#FBBF24',
                  color: 'var(--bg-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600',
                  fontSize: '18px',
                  flexShrink: 0
                }}>
                  {day.day}
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '18px', marginBottom: '8px', color: 'var(--text-primary)' }}>
                    {day.title}
                  </h4>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {day.activities}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px' }}>
              This itinerary can be customized based on your interests, timeline, and preferred regions.
            </p>
          </div>
        </div>
      </section>

      {/* DESTINATIONS */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '16px' }}>
            Coffee Regions of Indonesia
          </h2>
          <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '48px', maxWidth: '700px', margin: '0 auto 48px' }}>
            From Sumatra's legendary estates to Papua's emerging micro-lots, Indonesia's coffee landscape spans thousands of kilometers and countless terroirs.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '24px'
          }}>
            {coffeeCivilization.geographicGroups.map(group => (
              <Link
                key={group}
                to={`/indonesia-journeys/${group}`}
                style={{
                  padding: '24px',
                  background: 'var(--bg-primary)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  borderRadius: '4px',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#FBBF24';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.2)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <h4 style={{ fontSize: '18px', color: '#FBBF24', marginBottom: '8px', textTransform: 'capitalize' }}>
                  {group.replace('-', ' & ')}
                </h4>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                  Explore coffee experiences →
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CREDIBILITY */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '48px' }}>
            Why Travel with Indotravelista
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                title: 'Destination Expertise',
                description: 'Deep knowledge of Indonesia coffee-growing regions, from heritage estates to emerging specialty origins across the archipelago.'
              },
              {
                title: 'Curated Access',
                description: 'Direct relationships with producers, roasters, and specialty coffee professionals provide privileged access beyond standard tours.'
              },
              {
                title: 'Tailor-Made Journeys',
                description: 'Every Coffee Civilization itinerary is designed around your interests, timeline, and desired depth of engagement.'
              },
              {
                title: 'Cultural & Heritage Focus',
                description: 'Coffee as a gateway into Indonesian history, agriculture, trade, community life, and contemporary innovation.'
              }
            ].map((item, index) => (
              <div key={index} style={{ textAlign: 'center' }}>
                <CheckCircle size={40} style={{ color: '#FBBF24', marginBottom: '16px' }} />
                <h4 style={{ fontSize: '18px', marginBottom: '12px', color: 'var(--text-primary)' }}>
                  {item.title}
                </h4>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INQUIRY */}
      <section id="inquiry" className="section-padding" style={{ 
        background: `linear-gradient(rgba(0,0,0,0.85), rgba(0,0,0,0.85)), url(${coffeeCivilization.image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}>
        <div className="container" style={{ maxWidth: '900px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '40px', color: 'white', marginBottom: '24px' }}>
            Plan Your Coffee Civilization Journey
          </h2>
          <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.9)', marginBottom: '40px', maxWidth: '700px', margin: '0 auto 40px' }}>
            Whether you're a coffee professional seeking origin insights, an enthusiast pursuing deeper knowledge, or a traveler drawn to cultural immersion through coffee—we design journeys that match your interests.
          </p>

          {/* LEAD CAPTURE FORM */}
          <div style={{
            background: 'var(--bg-primary)',
            padding: '48px',
            borderRadius: '8px',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            {submitted ? (
              <div style={{ textAlign: 'center', color: '#FBBF24', padding: '32px 0' }}>
                <CheckCircle size={48} style={{ marginBottom: '16px' }} />
                <h3 style={{ fontSize: '24px', marginBottom: '12px' }}>Thank You!</h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                  We'll be in touch within 24 hours to discuss your Coffee Civilization journey.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
                <h3 style={{ fontSize: '24px', marginBottom: '24px', textAlign: 'center', color: 'var(--text-primary)' }}>
                  Enquire About Coffee Civilization
                </h3>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                      Country
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        borderRadius: '4px',
                        background: 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                      Number of Travelers
                    </label>
                    <input
                      type="text"
                      name="travelers"
                      value={formData.travelers}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        borderRadius: '4px',
                        background: 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Preferred Travel Period
                  </label>
                  <input
                    type="text"
                    name="travelPeriod"
                    placeholder="e.g., March 2026"
                    value={formData.travelPeriod}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Areas of Interest
                  </label>
                  <input
                    type="text"
                    name="interests"
                    placeholder="e.g., Specialty coffee, Origin visits, Processing methods"
                    value={formData.interests}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid rgba(148, 163, 184, 0.2)',
                      borderRadius: '4px',
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <button
                  type="submit"
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: '#FBBF24',
                    color: 'var(--bg-primary)',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  Submit Enquiry
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
